import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import useMyFetch from '@stores/useMyFetch'
import type { ReportTheme, ReportThemeListItem } from '@/types'
import { captureEvent } from '@/lib/analytics'

export const useReportThemesStore = defineStore('reportThemes', () => {
  const themes = ref<ReportThemeListItem[]>([])
  const currentTheme = ref<ReportTheme | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // With surveyToken: themes selectable for that survey (account's + system),
  // gated by survey membership server-side. Without: the actor's management
  // listing (admin/client theme pages).
  async function fetchThemes(surveyToken?: string) {
    loading.value = true
    error.value = null
    const url = surveyToken ? `/report_themes?survey_token=${surveyToken}` : '/report_themes'
    const { response, data } = await useMyFetch(url).get().json<ReportThemeListItem[]>()
    if (response.value.ok && data.value) {
      themes.value = data.value ?? []
    } else {
      error.value = 'Failed to fetch report themes'
    }
    loading.value = false
    return themes.value
  }

  async function fetchTheme(token: string) {
    loading.value = true
    error.value = null
    const { response, data } = await useMyFetch(`/report_themes/${token}`).get().json<ReportTheme>()
    if (response.value.ok && data.value) {
      currentTheme.value = data.value
      const index = themes.value.findIndex((t) => t.token === token)
      if (index !== -1) themes.value[index] = data.value
    } else if (!response.value.ok) {
      error.value = 'Failed to fetch report theme'
    }
    loading.value = false
    return currentTheme.value
  }

  async function createTheme(params: { name: string; account_ids?: number[] }) {
    loading.value = true
    error.value = null
    const { response, data } = await useMyFetch('/report_themes').post({ report_theme: params }).json<ReportTheme>()
    if (response.value.ok && data.value) {
      themes.value.unshift(data.value)
      loading.value = false
      captureEvent('report_theme_created', { report_theme_token: data.value.token })
      return data.value
    } else {
      error.value = 'Failed to create report theme'
      loading.value = false
      throw new Error(error.value)
    }
  }

  async function updateTheme(token: string, params: Partial<{ name: string; account_ids: number[]; text_styles: object; chart_styles: object }>) {
    const { response, data } = await useMyFetch(`/report_themes/${token}`).patch({ report_theme: params }).json<ReportTheme>()
    if (response.value.ok && data.value) {
      captureEvent('report_theme_updated', { report_theme_token: token })

      const index = themes.value.findIndex((t) => t.token === token)
      if (index !== -1) themes.value[index] = data.value
      if (currentTheme.value?.token === token) {
        currentTheme.value = { ...currentTheme.value, ...data.value }
      }
      return data.value
    } else {
      throw new Error('Failed to update report theme')
    }
  }

  async function deleteTheme(token: string) {
    const { response } = await useMyFetch(`/report_themes/${token}`).delete().json()
    if (response.value.ok || response.value.status === 204) {
      captureEvent('report_theme_deleted', { report_theme_token: token })

      themes.value = themes.value.filter((t) => t.token !== token)
      if (currentTheme.value?.token === token) currentTheme.value = null
      return true
    } else {
      throw new Error('Failed to delete report theme')
    }
  }

  async function uploadPptx(token: string, file: File) {
    const csrfToken = localStorage.getItem('csrf_token') ?? ''
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetch(`/api/report_themes/${token}/upload_pptx`, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken },
      body: formData,
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error((body as { error?: string }).error ?? 'Failed to upload PPTX')
    }
    const data = (await response.json()) as ReportTheme
    currentTheme.value = { ...currentTheme.value, ...data } as ReportTheme
    return data
  }

  // ── Local edit state (for dirty tracking + single Save) ──────────────────
  const localEdits = ref<Partial<ReportTheme> | null>(null)
  const saving = ref(false)

  const hasChanges = computed(() => {
    if (!localEdits.value || !currentTheme.value) return false
    return JSON.stringify(localEdits.value) !== JSON.stringify({
      name:         currentTheme.value.name,
      account_ids: currentTheme.value.account_ids,
      text_styles:  currentTheme.value.text_styles,
      chart_styles: currentTheme.value.chart_styles,
    })
  })

  function initEdits() {
    if (!currentTheme.value) return
    localEdits.value = {
      name:         currentTheme.value.name,
      account_ids: currentTheme.value.account_ids,
      text_styles:  JSON.parse(JSON.stringify(currentTheme.value.text_styles ?? {})),
      chart_styles: JSON.parse(JSON.stringify(currentTheme.value.chart_styles ?? {})),
    }
  }

  async function saveEdits() {
    if (!currentTheme.value || !localEdits.value) return
    saving.value = true
    try {
      await updateTheme(currentTheme.value.token, localEdits.value)
    } finally {
      saving.value = false
    }
  }

  function clearCurrentTheme() {
    currentTheme.value = null
    localEdits.value = null
  }

  return {
    themes,
    currentTheme,
    localEdits,
    hasChanges,
    saving,
    loading,
    error,
    fetchThemes,
    fetchTheme,
    createTheme,
    updateTheme,
    deleteTheme,
    uploadPptx,
    initEdits,
    saveEdits,
    clearCurrentTheme,
  }
})
